import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, UtensilsCrossed, CalendarCheck } from "lucide-react";
import { scrollToId, resolveImg } from "@/lib/site";
import { useLanguage } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function HeroSection() {
  const { t, content } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden bg-[#082F38]"
    >
      <motion.div className="absolute inset-0" style={{ y: imageY, scale: imageScale }}>
        <div
          data-testid="hero-image"
          className="h-full w-full bg-gradient-to-b from-black via-[#062430] to-[#0D5C63]"
        />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-5 pt-36 pb-24 sm:px-8 lg:px-10"
      >
        <div
          data-testid="hero-logo"
          className="mx-auto mb-7"
        >
          <img
            src={resolveImg(content.images.heroLogo)}
            alt="Logo oficial do Quiosque Rainha do Mar"
            className="h-32 w-32 rounded-full object-cover drop-shadow-2xl sm:h-40 sm:w-40"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-[#E9C46A]/40 bg-[#082F38]/50 px-4 py-2 backdrop-blur-md"
          data-testid="hero-location-badge"
        >
          <MapPin size={14} className="text-[#E9C46A]" />
          <span className="font-jetmono text-[10px] uppercase tracking-[0.28em] text-[#F4EBD9] sm:text-[11px]">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="max-w-4xl text-4xl font-bold leading-[1.08] tracking-tight text-[#FAF6EE] sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.45} className="font-cabinet font-black">
            {t.hero.line1}
          </MaskedLine>
          <MaskedLine delay={0.6} className="font-playfair font-semibold italic text-[#E9C46A]">
            {t.hero.line2}
          </MaskedLine>
          {t.hero.line3 ? (
            <MaskedLine delay={0.75} className="font-cabinet font-black">
              {t.hero.line3}
            </MaskedLine>
          ) : null}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#F4EBD9]/85 sm:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-menu"
            onClick={() => scrollToId("cardapio")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#E76F51] px-7 py-3.5 font-cabinet font-bold text-[#FAF6EE] transition-all duration-300 hover:-translate-y-1 hover:bg-[#D85C3D] hover:shadow-xl hover:shadow-[#E76F51]/30"
          >
            <UtensilsCrossed size={17} className="transition-transform duration-300 group-hover:rotate-12" />
            {t.hero.ctaMenu}
          </button>
          <button
            data-testid="hero-cta-reserve"
            onClick={() => scrollToId("reservar")}
            className="inline-flex items-center gap-2.5 rounded-full border border-[#FAF6EE]/40 px-7 py-3.5 font-cabinet font-bold text-[#FAF6EE] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#E9C46A] hover:text-[#E9C46A]"
          >
            <CalendarCheck size={17} />
            {t.hero.ctaReserve}
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-stats-pill"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-2 font-jetmono text-[10px] uppercase tracking-[0.22em] text-[#F4EBD9]/70 sm:text-xs"
        >
          {t.hero.stats.map((stat, i) => (
            <span key={stat} className="flex items-center gap-5">
              {i > 0 && <span className="text-[#E9C46A]">✦</span>}
              {stat}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("cardapio")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute right-7 bottom-7 z-10 hidden h-12 w-12 place-items-center rounded-full border border-[#FAF6EE]/30 text-[#FAF6EE]/80 md:grid"
        aria-label={t.hero.scrollAria}
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
}
